
import { useState } from 'react';
import { useCart } from '../../../contexts/CartContext';

const ServicesList = () => {
  const { addItem, isInCart } = useCart();
  const [addedStates, setAddedStates] = useState<Record<string, boolean>>({});

  const services = [
    {
      id: 'helpdesk-support',
      icon: 'ri-customer-service-2-line',
      title: 'IT Helpdesk & Support',
      price: 100,
      priceDisplay: '$160',
      description: 'Round-the-clock technical support for your team. Our helpdesk experts resolve issues quickly, answer questions, and keep your employees productive with responsive, friendly assistance whenever they need it.',
      features: [
        'Email & Phone Support',
        'Ticket Management System',
        'Remote Troubleshooting',
        'User Account Management'
      ],
      color: 'from-violet-500 to-purple-600',
      borderColor: 'border-violet-500'
    },
    {
      id: 'network-management',
      icon: 'ri-router-line',
      title: 'Network Setup & Management',
      price: 150,
      priceDisplay: '$250',
      description: 'Professional network design and ongoing management to ensure reliable, secure connectivity. We configure routers, switches, and wireless access points for optimal performance and security.',
      features: [
        'Network Design & Installation',
        'Router & Switch Configuration',
        'WiFi Setup & Optimization',
        'Network Security Monitoring'
      ],
      color: 'from-purple-500 to-fuchsia-600',
      borderColor: 'border-purple-500'
    },
    {
      id: 'cybersecurity-audit',
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity Audit & Protection',
      price: 200,
      priceDisplay: '$600',
      description: 'Comprehensive security assessments and protection measures to safeguard your business from cyber threats. We identify vulnerabilities and implement robust defenses to keep your data secure.',
      features: [
        'Security Vulnerability Scanning',
        'Firewall Configuration',
        'Antivirus & Malware Protection',
        'Security Policy Development'
      ],
      color: 'from-fuchsia-500 to-pink-600',
      borderColor: 'border-fuchsia-500'
    },
    {
      id: 'cloud-hosting',
      icon: 'ri-cloud-line',
      title: 'Cloud Hosting & Migration',
      price: 250,
      priceDisplay: '$150',
      description: 'Seamless cloud migration and reliable hosting services. We help you move to the cloud safely and manage your cloud infrastructure for maximum uptime, performance, and cost efficiency.',
      features: [
        'Cloud Platform Setup (AWS, Azure, Google)',
        'Data Migration Services',
        'Cloud Storage Management',
        'Performance Optimization'
      ],
      color: 'from-indigo-500 to-violet-600',
      borderColor: 'border-indigo-500'
    },
    {
      id: 'software-licensing',
      icon: 'ri-apps-line',
      title: 'Software Licensing & Management',
      price: 175,
      priceDisplay: '$175',
      description: 'Streamline your software assets with expert licensing management. We help you stay compliant, optimize costs, and ensure your team has the right tools without overspending on unused licenses.',
      features: [
        'License Procurement & Renewal',
        'Software Asset Tracking',
        'Compliance Management',
        'Volume Licensing Optimization'
      ],
      color: 'from-violet-500 to-indigo-600',
      borderColor: 'border-violet-500'
    },
    {
      id: 'it-training',
      icon: 'ri-graduation-cap-line',
      title: 'IT Training & Onboarding',
      price: 125,
      priceDisplay: '$125',
      description: 'Empower your team with comprehensive IT training programs. From new employee onboarding to advanced software training, we ensure your staff has the skills to use technology effectively.',
      features: [
        'New Employee IT Orientation',
        'Software Training Sessions',
        'Security Awareness Training',
        'Custom Training Materials'
      ],
      color: 'from-purple-500 to-violet-600',
      borderColor: 'border-purple-500'
    },
    {
      id: 'remote-monitoring',
      icon: 'ri-radar-line',
      title: 'Remote Monitoring & Management',
      price: 750,
      priceDisplay: '$750',
      description: 'Proactive 24/7 monitoring of your entire IT infrastructure. Our advanced RMM platform detects issues before they become problems, performs automated maintenance, and ensures peak system performance.',
      features: [
        '24/7 System Monitoring',
        'Automated Patch Management',
        'Performance Analytics & Reporting',
        'Predictive Maintenance Alerts'
      ],
      color: 'from-fuchsia-500 to-violet-600',
      borderColor: 'border-fuchsia-500'
    }
  ];

  const handleBuyService = (service: typeof services[0]) => {
    addItem({
      id: service.id,
      name: service.title,
      price: service.price,
      icon: service.icon,
      color: service.color
    });

    setAddedStates(prev => ({ ...prev, [service.id]: true }));
    
    setTimeout(() => {
      setAddedStates(prev => ({ ...prev, [service.id]: false }));
    }, 2000);
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">Our Service Offerings</h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            Flexible IT solutions with transparent pricing to fit businesses of all sizes
          </p>
        </div>

        <div className="space-y-6 md:space-y-8">
          {services.map((service, index) => {
            const inCart = isInCart(service.id);
            const justAdded = addedStates[service.id];
            
            return (
              <div 
                key={index}
                className={`bg-white rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 ${service.borderColor}`}
              >
                <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8 lg:items-start">
                  {/* Main Content */}
                  <div className="lg:col-span-8">
                    <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-6">
                      {/* Icon */}
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br ${service.color} rounded-xl md:rounded-2xl flex-shrink-0 mx-auto sm:mx-0`}>
                        <i className={`${service.icon} text-white text-xl sm:text-2xl md:text-3xl`}></i>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 text-center sm:text-left">
                        {/* Title and Price */}
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3">
                          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">{service.title}</h3>
                          <span className="text-lg sm:text-xl md:text-2xl font-bold text-violet-600">{service.priceDisplay}</span>
                        </div>
                        
                        {/* Description */}
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                          {service.description}
                        </p>
                        
                        {/* Buy Button */}
                        <button
                          onClick={() => handleBuyService(service)}
                          disabled={inCart}
                          className={`w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all whitespace-nowrap cursor-pointer ${
                            inCart
                              ? 'bg-green-500 text-white cursor-not-allowed'
                              : justAdded
                              ? 'bg-green-500 text-white'
                              : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700'
                          }`}
                        >
                          {inCart ? (
                            <>
                              <i className="ri-check-line text-base sm:text-lg"></i>
                              <span>In Cart</span>
                            </>
                          ) : justAdded ? (
                            <>
                              <i className="ri-check-line text-base sm:text-lg"></i>
                              <span>Added</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-shopping-cart-line text-base sm:text-lg"></i>
                              <span>Buy Service</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Features Sidebar */}
                  <div className="lg:col-span-4">
                    <div className="bg-gray-50 rounded-lg md:rounded-xl p-4 md:p-6">
                      <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-3 md:mb-4 uppercase tracking-wider text-center lg:text-left">Key Features</h4>
                      <ul className="space-y-2 md:space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <i className="ri-check-line text-green-500 text-base sm:text-lg flex-shrink-0 mt-0.5"></i>
                            <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesList;
